import { toE164Phone } from './detectors';

// ----------------------------------------------------------------------
// Per-conversation map between placeholders and the real values behind them.
// It never leaves our systems: it lives in the provider and is stored next to
// the transcript in our own backend, never in anything sent to the model.
// ----------------------------------------------------------------------

export type PlaceholderKind = 'PHONE' | 'EMAIL' | 'ID';

/**
 * Where a value was found — decides which spelling is restored. `field`: an
 * API field such as `phone`, spelled the way the API stores it. `text`: found
 * inside free text (a message, a comment), spelled however someone typed it.
 */
export type ValueSource = 'field' | 'text';

type Entry = {
  kind: PlaceholderKind;
  value: string;
  source: ValueSource;
};

export type SerializedVault = {
  version: 1;
  entries: Array<Entry & { token: string }>;
};

export const PLACEHOLDER_PATTERN = /\[\[(?:(PHONE|EMAIL|ID)_\d+|REDACTED(?:_[A-Z]+)?)\]\]/g;

const PLACEHOLDER_KINDS: ReadonlySet<string> = new Set(['PHONE', 'EMAIL', 'ID']);

/** Two spellings of the same phone or email share one placeholder. */
const normalize = (kind: PlaceholderKind, value: string): string => {
  if (kind === 'PHONE') return toE164Phone(value);
  if (kind === 'EMAIL') return value.trim().toLowerCase();
  return value.replace(/\s/g, '').toUpperCase();
};

/**
 * The value an API receives when the placeholder is restored. An API field
 * keeps its own spelling, so searching by it matches. A phone typed in free
 * text ("90 123 45 67") becomes E.164, the format the app's phone input sends.
 */
const restorable = (kind: PlaceholderKind, value: string, source: ValueSource): string =>
  kind === 'PHONE' && source === 'text' ? toE164Phone(value) : value.trim();

export class PrivacyVault {
  private tokensByKey = new Map<string, string>();

  private entriesByToken = new Map<string, Entry>();

  private counters: Record<PlaceholderKind, number> = { PHONE: 0, EMAIL: 0, ID: 0 };

  tokenFor(kind: PlaceholderKind, value: string, source: ValueSource): string {
    const key = `${kind}:${normalize(kind, value)}`;
    const existing = this.tokensByKey.get(key);

    if (existing) {
      const entry = this.entriesByToken.get(existing);
      // The API's own spelling beats a typed one: it is what the API searches by.
      if (entry && entry.source === 'text' && source === 'field') {
        this.entriesByToken.set(existing, { kind, value: restorable(kind, value, source), source });
      }
      return existing;
    }

    this.counters[kind] += 1;
    const token = `[[${kind}_${this.counters[kind]}]]`;
    this.tokensByKey.set(key, token);
    this.entriesByToken.set(token, { kind, value: restorable(kind, value, source), source });
    return token;
  }

  valueOf(token: string): string | undefined {
    return this.entriesByToken.get(token)?.value;
  }

  toJSON(): SerializedVault {
    return {
      version: 1,
      entries: Array.from(this.entriesByToken, ([token, entry]) => ({ token, ...entry })),
    };
  }

  /** Stored data is untrusted: anything malformed is skipped, never thrown. */
  static fromJSON(data: unknown): PrivacyVault {
    const vault = new PrivacyVault();
    if (typeof data !== 'object' || data === null) return vault;

    const { entries } = data as { entries?: unknown };
    if (!Array.isArray(entries)) return vault;

    entries.forEach((raw: unknown) => {
      if (typeof raw !== 'object' || raw === null) return;
      const { token, kind, value, source } = raw as Record<string, unknown>;
      if (
        typeof token !== 'string' ||
        typeof kind !== 'string' ||
        !PLACEHOLDER_KINDS.has(kind) ||
        typeof value !== 'string' ||
        (source !== 'field' && source !== 'text')
      ) {
        return;
      }

      const placeholderKind = kind as PlaceholderKind;
      const number = Number(token.match(/_(\d+)\]\]$/)?.[1]);
      if (!token.startsWith(`[[${placeholderKind}_`) || !Number.isInteger(number)) return;

      vault.tokensByKey.set(`${placeholderKind}:${normalize(placeholderKind, value)}`, token);
      vault.entriesByToken.set(token, { kind: placeholderKind, value, source });
      vault.counters[placeholderKind] = Math.max(vault.counters[placeholderKind], number);
    });

    return vault;
  }
}
